import { useState } from 'react';

/**
 * Somebody's face, or their initials.
 *
 * ## Why this needs no data plumbed to it
 *
 * It takes a user id and a name, and nothing else. The photo is fetched from
 * `/profile/avatar/:userId`, and when there isn't one the request 404s and the
 * component falls back to initials.
 *
 * The alternative was threading a `hasAvatar` flag through every endpoint that
 * returns a person — the users list, the roster, ticket assignees, news
 * comments, the audit log. That is a dozen response shapes to change and a
 * dozen chances to forget one, in exchange for avoiding a handful of 404s on
 * small images. Not worth it: this way a face appears anywhere a name does,
 * the moment the component is dropped in.
 *
 * ## Initials
 *
 * Two letters at most, from the first and last word. The background colour is
 * derived from the id rather than random, so the same person is the same colour
 * on every screen and in every session — which is most of what makes an avatar
 * useful for recognition at a glance.
 */

/**
 * Deliberately drawn from the navy family rather than a rainbow.
 *
 * Orange is the action colour in this application and appears once per screen;
 * a wall of orange initials would eat that signal entirely. These are all
 * quiet enough to sit behind white text and distinct enough to tell people
 * apart.
 */
const TONES = [
  'bg-navy-700',
  'bg-navy-600',
  'bg-navy-800',
  'bg-slate-600',
  'bg-teal-700',
  'bg-indigo-700',
  'bg-stone-600',
  'bg-cyan-800',
];

/** Stable across sessions and machines: same id, same colour, always. */
function toneFor(id: string): string {
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) {
    hash = (hash * 31 + id.charCodeAt(index)) >>> 0;
  }
  return TONES[hash % TONES.length]!;
}

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words[words.length - 1]![0]}`.toUpperCase();
}

const SIZES = {
  sm: 'h-6 w-6 text-[10px]',
  md: 'h-8 w-8 text-xs',
  lg: 'h-12 w-12 text-sm',
  xl: 'h-24 w-24 text-xl',
} as const;

export function Avatar({
  userId,
  name,
  size = 'md',
  className = '',
}: {
  /** Null for somebody with no account — a technician who only gets links. */
  userId: string | null | undefined;
  name: string;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const shape = `${SIZES[size]} shrink-0 rounded-full object-cover ${className}`;

  if (userId && !failed) {
    return (
      <img
        src={`/api/v1/profile/avatar/${userId}`}
        alt=""
        // Decorative: the name is always beside it in the markup, so a screen
        // reader announcing "photo of Mary Kemunto, Mary Kemunto" would be
        // noise rather than help.
        aria-hidden="true"
        className={shape}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={`${shape} inline-flex items-center justify-center font-semibold uppercase leading-none text-white ${toneFor(userId ?? name)}`}
      /*
       * No `title`, deliberately.
       *
       * The browser tooltip it produces waits a second, renders in a style
       * nobody can read on a phone in the sun, and never appears at all for
       * somebody driving the page with Tab. This application replaced them
       * with `Tooltip` everywhere, and verify-hover.mjs guards the header
       * against a regression — which is how this one was caught.
       *
       * Nothing is lost by removing it: the element is `aria-hidden` and the
       * person's name is always rendered beside it.
       */
    >
      {initialsOf(name)}
    </span>
  );
}
